import type { ComponentType, KeyboardEvent } from "react";
import { useMemo, useRef, useState } from "react";
import { searchSettingsNavGroups } from "./settingsNavSearch.logic";
import {
  ArchiveIcon,
  BotIcon,
  CloudIcon,
  GitBranchIcon,
  KeyboardIcon,
  LaptopIcon,
  PaintbrushIcon,
  PanelTopIcon,
  RefreshCwIcon,
  ServerCogIcon,
  WorkflowIcon,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { SettingsSidebarLogout } from "./SettingsSidebarLogout";
import { scrollToSettingsTarget } from "./settingsLayout";
import {
  SETTINGS_SECTION_LABELS,
  SETTINGS_SECTIONS_REQUIRING_CMMD_SYNC,
  type SettingsSearchItem,
  type SettingsSearchPath,
} from "./settingsSearch";
import {
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../ui/sidebar";

import {
  SettingsRailHeader,
  SettingsSearchResults,
  buildSearchOptions,
  searchKeyAction,
  nextResultIndex,
  useSettingsRowSearch,
} from "./settingsRailSearch";

/**
 * The rail's icon and one-line description per destination.
 *
 * Keyed by path rather than listed as items, because the item list itself is
 * derived from `SETTINGS_SECTION_LABELS`: a `Record<SettingsSearchPath, …>`
 * cannot omit a destination or invent one, so the rail cannot drift from the
 * labels that search shows. The previous shape — a hand-written array of groups
 * in `settingsNavItems.ts` — held the same facts twice and needed a test to
 * assert the two agreed.
 */
const SETTINGS_SECTION_ICONS: Readonly<
  Record<SettingsSearchPath, ComponentType<{ className?: string }>>
> = {
  "/settings/general": ServerCogIcon,
  "/settings/diagnostics": CloudIcon,
  "/settings/appearance": PaintbrushIcon,
  "/settings/keybindings": KeyboardIcon,
  "/settings/providers": BotIcon,
  "/settings/agents": WorkflowIcon,
  "/settings/environments": LaptopIcon,
  "/settings/browser": PanelTopIcon,
  "/settings/git": GitBranchIcon,
  "/settings/cmmd": RefreshCwIcon,
  "/settings/archived": ArchiveIcon,
};

const SETTINGS_SECTION_DESCRIPTIONS: Readonly<Record<SettingsSearchPath, string>> = {
  "/settings/general": "Account, application defaults, and version.",
  "/settings/diagnostics":
    "Environment health. Machine evidence after Recheck for Platform admins.",
  "/settings/appearance": "Theme, typography, and reading preferences.",
  "/settings/keybindings": "Keyboard shortcuts and the contexts each applies in.",
  "/settings/providers": "Model providers, access modes, and runtime paths.",
  "/settings/agents": "Orchestration, context, threads, composer, and editor defaults.",
  "/settings/environments": "Runtime connections and paired devices.",
  "/settings/browser": "In-app browser use, data retention, and site permissions.",
  "/settings/git": "GitHub agent tools, generated Git text, and commit metadata.",
  "/settings/cmmd": "CMMD workspace sync.",
  "/settings/archived": "Archived work and thread recovery.",
};

export interface SettingsNavItem {
  readonly label: string;
  readonly to: SettingsSearchPath;
  readonly description: string;
  readonly icon: ComponentType<{ className?: string }>;
}

/** Every destination, in rail order, derived from the labels record. */
export const SETTINGS_NAV_ITEMS: ReadonlyArray<SettingsNavItem> = (
  Object.keys(SETTINGS_SECTION_LABELS) as SettingsSearchPath[]
).map((to) => ({
  to,
  label: SETTINGS_SECTION_LABELS[to],
  description: SETTINGS_SECTION_DESCRIPTIONS[to],
  icon: SETTINGS_SECTION_ICONS[to],
}));

/**
 * The destinations this viewer may reach.
 *
 * Replaces `getVisibleSettingsNavGroups`. Flattening the rail removed the group
 * that CMMD-Sync gating used to hang off, so the gate moved to the path itself.
 */
export function getVisibleSettingsNavItems({
  cmmdSyncPanelEnabled,
}: {
  readonly cmmdSyncPanelEnabled: boolean;
}): ReadonlyArray<SettingsNavItem> {
  return SETTINGS_NAV_ITEMS.filter(
    (item) => cmmdSyncPanelEnabled || !SETTINGS_SECTIONS_REQUIRING_CMMD_SYNC.has(item.to),
  );
}

function SettingsNavList({
  items,
  onNavigate,
  pathname,
}: {
  readonly items: ReadonlyArray<SettingsNavItem>;
  readonly onNavigate: (to: SettingsSearchPath) => void;
  readonly pathname: string;
}) {
  return (
    <SidebarGroup className="p-[var(--sidebar-content-inset)]">
      <SidebarMenu>
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === item.to;
          return (
            <SidebarMenuItem key={item.to} data-settings-nav-item={item.to}>
              <SidebarMenuButton
                size="sm"
                isActive={isActive}
                className={
                  isActive
                    ? "gap-2.5 px-[var(--sidebar-row-content-inset)] py-2 text-left text-[13px] font-medium text-sidebar-foreground"
                    : "gap-2.5 px-[var(--sidebar-row-content-inset)] py-2 text-left text-[13px] text-sidebar-muted-foreground hover:text-sidebar-foreground"
                }
                onClick={() => onNavigate(item.to)}
                title={item.description}
              >
                {Icon ? (
                  <Icon
                    className={
                      isActive
                        ? "size-4 shrink-0 text-sidebar-foreground"
                        : "size-4 shrink-0 text-[color:var(--sidebar-icon-color)]"
                    }
                  />
                ) : null}
                <span className="truncate">{item.label}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

export function SettingsSidebarNav({ pathname }: { pathname: string }) {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const cmmdSyncPanelEnabled = useFeatureFlag("cmmd_settings_sync_panel");
  const [query, setQuery] = useState("");
  // Scrolling the active option into view is a response to an arrow key, not to
  // results appearing; see `useActiveResultVisible`.
  const [hasNavigated, setHasNavigated] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const visibleItems = getVisibleSettingsNavItems({ cmmdSyncPanelEnabled });
  // Search runs over the ALREADY flag-filtered items, so no query can surface a
  // section this viewer is not meant to see. The ranking helper is shaped around
  // groups; a flat rail is one group whose label nothing renders.
  const searchedItems = useMemo(
    () =>
      searchSettingsNavGroups([{ label: "", items: visibleItems }], query).flatMap(
        (group) => group.items,
      ),
    [visibleItems, query],
  );
  const searching = query.trim().length > 0;

  const navigateTo = (to: SettingsSearchPath | "/") => {
    if (isMobile) {
      setOpenMobile(false);
    }
    void navigate({ to, replace: true });
  };

  const openResult = (item: SettingsSearchItem) => {
    setQuery("");
    if (isMobile) {
      setOpenMobile(false);
    }
    // Already on the page: the row is mounted, so jump straight to it. A
    // navigation to the route we are on would be a no-op and the hash would
    // never fire.
    if (pathname === item.to && scrollToSettingsTarget(item.id)) return;
    void navigate({ to: item.to, hash: item.id, replace: true });
  };

  const rowSearch = useSettingsRowSearch(query, cmmdSyncPanelEnabled, openResult);
  const { activeResultIndex, open, results, setActiveResultIndex } = rowSearch;

  // Sections and rows are ONE option list, in display order, so the arrows and
  // Enter address everything on screen rather than only the rows.
  const options = buildSearchOptions(
    searchedItems.map((item) => ({ label: item.label, to: item.to, icon: item.icon })),
    results,
  );
  const activeOption = options[activeResultIndex];

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const action = searchKeyAction(event.key, {
      composing: event.nativeEvent.isComposing,
      hasResults: options.length > 0,
      searching,
    });
    if (action === "ignore") return;
    event.preventDefault();
    // Escape must not bubble: on mobile the rail is a dialog, and the same
    // keystroke would close the whole sheet out from under someone who only
    // meant to clear what they typed.
    if (action === "clear") {
      event.stopPropagation();
      setQuery("");
      setActiveResultIndex(0);
      return;
    }
    if (action === "open") {
      if (activeOption?.kind === "row") open(activeOption.item);
      else if (activeOption?.kind === "section")
        navigateTo(activeOption.section.to as SettingsSearchPath);
      return;
    }
    setHasNavigated(true);
    setActiveResultIndex((index) =>
      nextResultIndex(index, action === "next" ? "ArrowDown" : "ArrowUp", options.length),
    );
  };

  const nothingMatched = searching && searchedItems.length === 0 && results.length === 0;

  return (
    <SidebarContent
      className="overflow-x-hidden"
      // Back and search are how you leave and how you find. Both were inside
      // the scroller and left with the nav groups.
      fixedHeader={
        <SettingsRailHeader
          activeResultId={activeOption?.id}
          onBack={() => navigateTo("/")}
          onKeyDown={handleSearchKeyDown}
          query={query}
          searchInputRef={searchInputRef}
          setQuery={(value) => {
            setQuery(value);
            setActiveResultIndex(0);
            setHasNavigated(false);
          }}
        />
      }
    >
      {nothingMatched ? (
        <div
          className="px-[calc(var(--sidebar-content-inset)+var(--sidebar-row-content-inset))] py-6 text-center text-xs text-sidebar-muted-foreground"
          data-testid="settings-nav-search-empty"
          // Focus stays in the field while this appears elsewhere in the rail,
          // so without a live region a screen-reader user is told nothing at
          // all -- the search simply goes quiet.
          role="status"
          aria-live="polite"
        >
          No settings match “{query.trim()}”.
        </div>
      ) : null}
      {searching ? (
        <SettingsSearchResults
          activeOptionId={activeOption?.id}
          hasNavigated={hasNavigated}
          onActivate={setActiveResultIndex}
          onOpenRow={open}
          onOpenSection={(to) => navigateTo(to as SettingsSearchPath)}
          options={options}
        />
      ) : (
        <SettingsNavList items={searchedItems} onNavigate={navigateTo} pathname={pathname} />
      )}
      {searching ? null : <SettingsSidebarLogout />}
    </SidebarContent>
  );
}
